import { useState } from "react";
import { toast } from "sonner";
import { Header, Footer } from "@/components/SiteChrome";
import api from "@/lib/api";

const SERVICES = ["Fitment consult", "Install slot", "Recovery training", "Trail day sign-up"];

export default function Bookings() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", rig: "", service: SERVICES[0], preferred_date: "", notes: "" });
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/bookings", form);
            setSent(true);
            toast.success("Booking request sent");
        } catch {
            toast.error("Something went wrong. Try again?");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-[hsl(var(--dust-50))]" data-testid="bookings-page">
            <Header />
            <div className="mx-auto grid max-w-6xl gap-16 px-4 py-20 sm:px-6 md:grid-cols-2">
                <div>
                    <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--ink-soft))]">Bookings</div>
                    <h1 className="mt-3 font-display text-5xl uppercase italic md:text-6xl text-[hsl(var(--ink))]">Book the bay.</h1>
                    <p className="mt-6 text-[hsl(var(--ink-soft))]">
                        Book a fitment consult, an install slot, or a spot on the next trail day. We'll follow
                        up within one business day.
                    </p>
                    <ul className="mt-10 space-y-4">
                        {[
                            ["Fitment consult", "30-min video call — we'll walk your build and recommend real parts."],
                            ["Install slot", "In-shop install with certified tech · SLC only."],
                            ["Recovery training", "Half-day trail class — winch, kinetic recovery, rig strategy."],
                            ["Trail day sign-up", "Monthly community run — RSVP so we can pack the group properly."],
                        ].map(([h, b]) => (
                            <li key={h} className="border-l-4 border-[hsl(var(--rust))] pl-4">
                                <div className="font-heading text-sm uppercase tracking-wider text-[hsl(var(--ink))]">{h}</div>
                                <p className="mt-1 text-sm text-[hsl(var(--ink-soft))]">{b}</p>
                            </li>
                        ))}
                    </ul>
                </div>

                <form onSubmit={submit} className="space-y-4 border-2 border-[hsl(var(--ink))] bg-[hsl(var(--dust-300))] p-8 text-[hsl(var(--dust-50))]">
                    {sent ? (
                        <div className="text-center" data-testid="bookings-sent">
                            <div className="font-display text-4xl uppercase italic">Booked. We'll confirm shortly.</div>
                            <p className="mt-3 text-[hsl(var(--dust-100))]">Check your email in the next 24 hours.</p>
                        </div>
                    ) : (
                        <>
                            {["name", "email", "phone", "rig"].map((k) => (
                                <div key={k}>
                                    <label className="font-heading text-xs uppercase tracking-wider">{k}</label>
                                    <input
                                        required={k === "name" || k === "email"}
                                        type={k === "email" ? "email" : "text"}
                                        value={form[k]}
                                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                        className="mt-1 w-full border border-[hsl(var(--ink))] bg-[hsl(var(--dust-50))] px-3 py-2.5 font-mono-brand text-sm text-[hsl(var(--ink))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                        data-testid={`booking-${k}`}
                                    />
                                </div>
                            ))}
                            <div>
                                <label className="font-heading text-xs uppercase tracking-wider">Service</label>
                                <select
                                    value={form.service}
                                    onChange={(e) => setForm({ ...form, service: e.target.value })}
                                    className="mt-1 w-full border border-[hsl(var(--ink))] bg-[hsl(var(--dust-50))] px-3 py-2.5 font-mono-brand text-sm text-[hsl(var(--ink))]"
                                    data-testid="booking-service"
                                >
                                    {SERVICES.map((s) => <option key={s}>{s}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="font-heading text-xs uppercase tracking-wider">Preferred date</label>
                                <input
                                    required
                                    type="date"
                                    value={form.preferred_date}
                                    onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                                    className="mt-1 w-full border border-[hsl(var(--ink))] bg-[hsl(var(--dust-50))] px-3 py-2.5 font-mono-brand text-sm text-[hsl(var(--ink))]"
                                    data-testid="booking-date"
                                />
                            </div>
                            <div>
                                <label className="font-heading text-xs uppercase tracking-wider">Notes</label>
                                <textarea
                                    rows={3}
                                    value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    className="mt-1 w-full border border-[hsl(var(--ink))] bg-[hsl(var(--dust-50))] px-3 py-2.5 font-mono-brand text-sm text-[hsl(var(--ink))]"
                                    data-testid="booking-notes"
                                />
                            </div>
                            <button type="submit" disabled={busy} className="w-full border-2 border-[hsl(var(--ink))] bg-[hsl(var(--primary))] py-4 font-heading text-sm uppercase tracking-wider text-[hsl(var(--ink))] hover:bg-[hsl(var(--primary-hover))] disabled:opacity-60" data-testid="booking-submit">
                                {busy ? "Sending…" : "Request booking →"}
                            </button>
                        </>
                    )}
                </form>
            </div>
            <Footer />
        </div>
    );
}
